export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#f4f4f2] dark:bg-dark-950">
      <div className="flex-1 flex flex-col relative overflow-hidden py-8 sm:py-12 lg:py-16">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.04)_1px,transparent_1px)] bg-[size:44px_44px]" />
        <div className="flex-1 flex justify-center p-4 sm:p-6 lg:p-8">
          <div className="relative z-10 w-full max-w-4xl">
            <div className="bg-white dark:bg-dark-900 border-2 border-zinc-900 dark:border-dark-700 p-6 sm:p-8 lg:p-10 space-y-6 sm:space-y-8">
              {/* Header */}
              <div className="text-center space-y-4 sm:space-y-6">
                <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 bg-zinc-900 flex items-center justify-center border-2 border-zinc-900">
                  <svg
                    className="w-7 h-7 sm:w-8 sm:h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
                  Privacy Policy
                </h1>
                <p className="text-base sm:text-lg text-gray-600 dark:text-dark-400">
                  Last updated: August 13, 2026
                </p>
              </div>

              {/* Content */}
              <div className="space-y-6 sm:space-y-8 prose prose-sm sm:prose-base max-w-none">
                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Introduction
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    DealHawk, LLC ("DealHawk," "we," "us," or "our") respects your privacy. This policy explains how we collect, use, disclose, and protect information when you use DealHawk websites and mobile apps, including the Penny Meals mobile app.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Information We Collect
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-200 mb-2">
                        Account Information
                      </h3>
                      <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed">
                        When you create an account, we collect your email address, an account identifier, your password in hashed form, and any optional profile information you provide. Penny Meals also stores the ZIP code and grocery stores you choose so it can show relevant local deals.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-200 mb-2">
                        Usage Data
                      </h3>
                      <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed">
                        We may collect information about how you interact with our services, such as feature use and selected preferences. In Penny Meals, deal searches, saved deals, and meal-basket selections remain on your device unless a feature clearly tells you otherwise.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-200 mb-2">
                        Device Information
                      </h3>
                      <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed">
                        We and our service providers may process limited technical information such as device type, operating system, browser type, IP address, and request timestamps to deliver the service, prevent abuse, and maintain security.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-200 mb-2">
                        Community Content and Activity
                      </h3>
                      <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-3">
                        If you use Community Success, we collect the photos, post messages, and comments you submit; the first name or community display name shown with them; and related identifiers and timestamps. We also collect community activity such as likes, reports, report reasons, blocked-member relationships, and records showing whether and when you accepted the community guidelines and confirmed that you are at least 18. The age gate records your confirmation and time of confirmation; it does not ask for your birth date.
                      </p>
                      <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-3">
                        We use photo-library access only so you can choose images for features such as profile photos and Community Success. We process and upload the images you select; we do not upload other photos from your library. Before a Community Success post or comment is published, its text and selected photos are processed by automated safety-moderation services. Content may be rejected and not published based on that review.
                      </p>
                      <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed">
                        Community Content is not private. It is displayed with your community display name to signed-in DealHawk members. Community photos are stored in cloud object storage and may be served through publicly accessible file URLs, so anyone who obtains a file URL may be able to view or reshare the image. Do not upload personal, financial, location, or other sensitive information. If you post in a DealHawk Discord channel that is identified as a source for Community Success, the post and its images may be copied into the DealHawk feed.
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Payment Information
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    We do not collect or store payment card details. Website payments may be processed by Stripe. Eligible mobile-app subscriptions are processed by Apple through the App Store, and RevenueCat receives purchase and subscription status information so we can validate purchases, unlock paid features, provide purchase restoration, and analyze subscription performance. We receive transaction and entitlement information, not your complete payment-card details.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    How We Use Your Information
                  </h2>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base text-gray-700 dark:text-dark-300">
                    <li>To provide and maintain our service</li>
                    <li>To notify you about changes to our service</li>
                    <li>To provide customer support</li>
                    <li>To personalize local deal results and selected-store coverage</li>
                    <li>To validate subscriptions, restore purchases, and prevent fraud</li>
                    <li>To publish and operate Community Success, including posts, comments, likes, reports, and blocks</li>
                    <li>To screen and review Community Content, enforce our rules, investigate reports, prevent abuse, and protect members</li>
                    <li>To analyze and improve our services</li>
                    <li>To detect, prevent and address technical issues</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Data Security
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Data Sharing
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    We do not sell your personal data. We may share your information only in the following circumstances:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base text-gray-700 dark:text-dark-300">
                    <li>With other signed-in members when you choose to publish Community Content, including your community display name, photos, messages, comments, and engagement counts</li>
                    <li>With service providers that help us operate the service, including Amazon Web Services for cloud hosting and storage, OpenAI for automated content-safety moderation, Discord for designated-channel imports and internal report notifications, analytics and error-reporting providers, Meta for advertising measurement and campaign attribution, Stripe for eligible website payments, Apple for App Store transactions, and RevenueCat for subscription management</li>
                    <li>With our moderation and support personnel so they can review reports, enforce our rules, and respond to requests</li>
                    <li>When required by law or to protect our rights</li>
                    <li>With your explicit consent</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Data Retention and Deletion
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    We retain information for as long as reasonably necessary to provide the service, maintain security, moderate content, resolve disputes, comply with law, and enforce our agreements. Retention may vary based on the type of information and why we process it.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    You can delete your own native Community Success posts and comments using the available in-app controls. Deleting a post or comment removes it from the active feed, but associated files or records may remain for a period in cloud storage, logs, backups, moderation records, or safety and legal records. Posts imported from a designated Discord channel cannot be deleted by their author from inside DealHawk; contact us to request removal of the copied post.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    Deleting your DealHawk account removes the account record and certain account data, but it does not automatically remove Community Content you shared before deletion. Delete that content first or email us at{" "}
                    <a
                      href="mailto:admin@dealhawkpro.io"
                      className="text-zinc-900 dark:text-white hover:text-zinc-700 dark:hover:text-zinc-300 font-semibold"
                    >
                      admin@dealhawkpro.io
                    </a>
                    {" "}to request removal. We may retain limited information where reasonably necessary for fraud prevention, safety, legal compliance, or a pending dispute.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Your Rights
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    You have the right to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base text-gray-700 dark:text-dark-300">
                    <li>Access your personal data</li>
                    <li>Correct inaccurate data</li>
                    <li>Request deletion of your data</li>
                    <li>Delete your native Community Success posts and comments using in-app controls</li>
                    <li>Object to processing of your data</li>
                    <li>Request data portability</li>
                    <li>Withdraw consent where consent is the basis for processing</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Cookies and Tracking
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    Our website may use cookies and similar technologies for authentication, preferences, security, service analytics, and advertising measurement. The Meta Pixel records website visits and selected funnel events, such as signup, checkout, and subscription milestones, so we can attribute and improve advertising campaigns. We do not send email addresses, ZIP codes, product identifiers, or account details through these Pixel events. Penny Meals does not use the data described in its App Store privacy label to track you across apps or websites owned by other companies for targeted advertising. You can configure browser cookie controls and applicable device privacy settings.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Changes to This Privacy Policy
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Contact Us
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    If you have any questions about this Privacy Policy, please contact us at{" "}
                    <a
                      href="mailto:admin@dealhawkpro.io"
                      className="text-zinc-900 dark:text-white hover:text-zinc-700 dark:hover:text-zinc-300 font-semibold"
                    >
                      admin@dealhawkpro.io
                    </a>
                  </p>
                </section>
              </div>

              {/* Back to App */}
              <div className="text-center pt-4 sm:pt-6">
                <a
                  href="/"
                  className="inline-flex items-center justify-center space-x-2 bg-zinc-900 hover:bg-black text-white font-semibold py-3 px-6 sm:py-4 sm:px-8 rounded-none transition-colors text-sm sm:text-base w-full sm:w-auto border-2 border-zinc-900"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  <span>Back to App</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
